import React, { useMemo } from 'react';
import JiraIntegration from '../JiraIntegration';
import { User, VotingSession } from '../../types';
import { VoteStats } from '../../utils/voteStats';

interface VoteResultsProps {
  session: VotingSession;
  votes: any[];
  participants: User[];
  moderatorId?: string;
  stats: VoteStats | null;
  /** Moderator-only "next session" control. */
  onNextSession?: () => void;
  /** Moderator-only "vote again on this story" control. */
  onResetVotes?: () => void;
  isEndingSession: boolean;
  isResetting?: boolean;
}

const VoteResults: React.FC<VoteResultsProps> = ({
  session,
  votes,
  participants,
  moderatorId,
  stats,
  onNextSession,
  onResetVotes,
  isEndingSession,
  isResetting = false,
}) => {
  const nameFor = (userId: string) =>
    participants.find((p) => p.id === userId)?.display_name || `User ${userId}`;

  // モデレーターを先頭に、その後は表示名順で並べる
  const sortedVotes = useMemo(() => {
    return [...votes].sort((a, b) => {
      if (moderatorId) {
        if (a.user_id === moderatorId && b.user_id !== moderatorId) return -1;
        if (b.user_id === moderatorId && a.user_id !== moderatorId) return 1;
      }
      return nameFor(a.user_id).localeCompare(nameFor(b.user_id));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [votes, participants, moderatorId]);

  return (
    <div className="vote-results card">
      <h4>投票結果</h4>

      {stats && (
        <div className="results-stats">
          <div className="stat-item">
            <span className="stat-label">平均値</span>
            <span className="stat-value">{stats.avg}</span>
          </div>
          <div className="stat-item">
            <span className="stat-label">最小値</span>
            <span className="stat-value">{stats.min}</span>
          </div>
          <div className="stat-item">
            <span className="stat-label">最大値</span>
            <span className="stat-value">{stats.max}</span>
          </div>
          <div className="stat-item">
            <span className="stat-label">投票数</span>
            <span className="stat-value">{stats.count}</span>
          </div>
        </div>
      )}

      <div className="individual-votes">
        <h5>個別投票結果</h5>
        <div className="votes-grid">
          {sortedVotes.map((vote) => (
            <div key={vote.user_id} className="vote-card-result">
              <span className="voter-name">{nameFor(vote.user_id)}</span>
              <span className="vote-value">{vote.value}</span>
            </div>
          ))}
        </div>
      </div>

      <JiraIntegration
        sessionId={session.id}
        storyTitle={session.story_title}
        averagePoints={stats ? parseFloat(stats.avg) : 0}
      />

      {(onNextSession || onResetVotes) && (
        <div className="session-actions">
          {onResetVotes && (
            <button onClick={onResetVotes} className="btn btn-secondary" disabled={isResetting}>
              {isResetting ? 'クリア中...' : 'もう一度投票する'}
            </button>
          )}
          {onNextSession && (
            <button onClick={onNextSession} className="btn btn-secondary" disabled={isEndingSession}>
              {isEndingSession ? 'セッション終了中...' : '次の投票に進む'}
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default VoteResults;
